import { FC, SyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { Tab, Tabs } from '@mui/material'

/**
 * Tab keys for the in-page tab navigation. Persisted to the URL as `?tab=…`
 * so deep-linking + browser back/forward keep the user on the right section.
 * `overview` is the implicit default and intentionally NOT written to the
 * URL, keeping the canonical entry URL clean — it's the at-a-glance landing
 * that summarises every other tab and routes the user onward.
 */
export const TAB_IDS = ['overview', 'motivations', 'story-patterns', 'creative-ads', 'creative-tags'] as const
export type TabId = (typeof TAB_IDS)[number]
export const DEFAULT_TAB: TabId = 'overview'

export const isTabId = (value: string | null): value is TabId => value !== null && (TAB_IDS as readonly string[]).includes(value)

interface GamePageTabsProps {
  activeTab: TabId
  onTabChange: (event: SyntheticEvent, nextTab: TabId) => void
  /** When true, render the smaller "compact" tab strip (used in the
   *  scrolled / collapsed sticky header). */
  compact: boolean
  disabled: boolean
}

/**
 * In-page tab navigation for the UA Insights game page (Overview /
 * Motivations / Story Patterns / Creative Ads / Creative Tags). Lives on its
 * own full-width row beneath the title in both header states; `compact` only
 * shrinks the strip (after the sticky header collapses) rather than relocating
 * it. The strip is horizontally scrollable so the tab set can keep growing
 * without crowding the title or the right-side controls — on narrow viewports
 * overflowing tabs scroll behind auto scroll buttons instead of wrapping.
 */
export const GamePageTabs: FC<GamePageTabsProps> = ({ activeTab, onTabChange, compact, disabled }) => {
  const { t } = useTranslation('ua-insights')

  const minHeight = compact ? 32 : 38

  return (
    <Tabs
      value={activeTab}
      onChange={onTabChange}
      textColor="primary"
      indicatorColor="primary"
      variant="scrollable"
      scrollButtons="auto"
      allowScrollButtonsMobile
      sx={{
        minHeight,
        // Nudge the row down 1px so the active-tab indicator sits flush on
        // top of the sticky header's bottom border instead of floating a
        // hairline above it.
        transform: 'translateY(1px)',
        '& .MuiTab-root': { minHeight, py: compact ? 0 : 0.5, px: compact ? 1.5 : 2, textTransform: 'none', fontWeight: 600 },
        // Hide the scroll-button slots when they're not needed so the strip
        // doesn't reserve dead space while every tab still fits.
        '& .MuiTabs-scrollButtons.Mui-disabled': { opacity: 0, width: 0 },
      }}
    >
      <Tab value="overview" label={t('tab_overview')} disabled={disabled} />
      <Tab value="motivations" label={t('tab_motivations')} disabled={disabled} />
      <Tab value="story-patterns" label={t('tab_story_patterns')} disabled={disabled} />
      <Tab value="creative-ads" label={t('tab_creative_ads')} disabled={disabled} />
      <Tab value="creative-tags" label={t('tab_creative_tags')} disabled={disabled} />
    </Tabs>
  )
}
